import { Wizard } from '@factorialco/deprecated-design-system'
import {
  MyPurchaseRequestsDataCollectionDocument,
  PurchaseOrderDetailDocument,
  PurchaseOrdersDataCollectionDocument,
  UpdateOrderModalDocument,
  UpdatePurchaseOrderDocument,
} from 'generated/resources/graphql'
import { compact } from 'lodash'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivities } from 'modules/core/lib/activities/provider'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ErrorModal from './ErrorModal'
import CostStep from './Steps/CostStep'
import GeneralInformationStep from './Steps/GeneralInformationStep'
import PaymentMethodStep from './Steps/PaymentMethodStep'
import VendorStep from './Steps/VendorStep'
import { WizardData } from './types'

interface Props {
  purchaseOrderId: string
  handleClose: () => void
}

export const UpdatePurchaseOrderModal: React.FC<Props> = ({ purchaseOrderId, handleClose }) => {
  const { company } = useCurrentSession()
  const { currentActivity } = useActivities()
  const [updatePurchaseOrder] = useMutation(UpdatePurchaseOrderDocument, {
    extractorFn: (data) => data.procurementMutations.updateProcurementPurchaseOrder,
    refetchQueries: [MyPurchaseRequestsDataCollectionDocument],
  })

  const {
    data: purchaseOrderFormData,
    loading,
    error,
  } = useQuery(UpdateOrderModalDocument, {
    variables: { companyId: company.id, purchaseOrderId: purchaseOrderId },
  })

  if (loading) return <Wizard.LoadingModal onClose={handleClose} />

  const purchaseOrder = purchaseOrderFormData?.procurement.purchaseOrder
  if (error || !purchaseOrder) return <ErrorModal onClose={handleClose} />

  const canCreateSingleUseCards =
    purchaseOrderFormData?.expenses._policies.cardRequestSingleUseCards.can
  const canManageContactsPaymentData =
    purchaseOrderFormData?.finance._policies.contactsManagePaymentData.can
  const cardsEnabled =
    purchaseOrderFormData?.features.featuresConnection.edges.some(
      (edge) => edge.node.name === 'dev_procurement_cards'
    ) ?? false

  const handleSubmit = async (data: WizardData) => {
    app.setProgress({
      message: i18n.t('procurement.purchase_orders.update_order_wizard.notifications.updating'),
    })

    const result = await updatePurchaseOrder({
      variables: {
        id: purchaseOrder.id,
        description: data.description,
        formattedPoNumber: data.formattedPoNumber,
        companyId: company.id,
        legalEntityId: data.legalEntityId,
        amount: data.purchaseOrderCost.cents,
        currency: data.purchaseOrderCost.currency,
        vendorId: data.vendor?.id,
        preferredPaymentMethod: data.preferredPaymentMethod,
        cardPaymentDetails:
          data.preferredPaymentMethod === 'virtualcard' && data.cardPaymentDetails?.cardholderData
            ? {
                cardAlias: data.cardPaymentDetails.cardAlias || '',
                amountLimitCents: data.cardPaymentDetails?.budgetFlexibility
                  ? Math.round(
                      data.purchaseOrderCost.cents *
                        (1 + data.cardPaymentDetails.budgetFlexibility / 100)
                    )
                  : data.purchaseOrderCost.cents,
                cardholderData: {
                  dateOfBirth:
                    data.cardPaymentDetails.cardholderData.dateOfBirth?.toISOString() || '',
                  firstName: data.cardPaymentDetails.cardholderData.firstName || '',
                  lastName: data.cardPaymentDetails.cardholderData.lastName || '',
                  phoneCountryCode: data.cardPaymentDetails.cardholderData.phoneCountryCode || '',
                  phoneNumber: data.cardPaymentDetails.cardholderData.phoneNumber || '',
                },
                employeeId: purchaseOrder.purchaseRequest?.requesterEmployee?.id ?? '0',
              }
            : undefined,
      },
      refetchQueries: [PurchaseOrdersDataCollectionDocument, PurchaseOrderDetailDocument],
    })
    app.setProgress(null)

    const handled = handleResult(result, (errors) => {
      if (errors.formattedPoNumber) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'procurement.purchase_orders.update_order_wizard.notifications.used_po_number'
          ),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message:
            Object.values(errors)[0] ??
            i18n.t('procurement.purchase_orders.update_order_wizard.notifications.error'),
        })
      }
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('procurement.purchase_orders.update_order_wizard.notifications.success'),
      })

      handleClose()
    }
  }

  const isGeneralInfoCompleted = (data: WizardData): boolean => {
    return !!data.formattedPoNumber && !!data.description
  }

  const isCostCompleted = (data: WizardData): boolean => {
    return (
      !!data.legalEntityId && !!data.purchaseOrderCost.cents && !!data.purchaseOrderCost.currency
    )
  }

  const isPaymentMethodCompleted = (data: WizardData): boolean => {
    if (data.preferredPaymentMethod === 'virtualcard') {
      if (data.cardPaymentDetails) {
        if (
          data.cardPaymentDetails.employeeId &&
          data.cardPaymentDetails.cardAlias &&
          data.cardPaymentDetails.cardholderData
        ) {
          const cardholder = data.cardPaymentDetails.cardholderData

          if (
            cardholder.dateOfBirth &&
            cardholder.firstName &&
            cardholder.lastName &&
            cardholder.lastName &&
            cardholder.phoneCountryCode &&
            cardholder.phoneNumber
          ) {
            return true
          }
        }
      }
    }

    if (data.preferredPaymentMethod === 'banktransfer') {
      if (data.bankPaymentDetails.bankCode && data.bankPaymentDetails.iban) {
        return true
      }
    }

    if (data.preferredPaymentMethod == 'cash') {
      return true
    }

    if (data.preferredPaymentMethod == undefined) {
      return true
    }

    return false
  }

  const employee = purchaseOrder.purchaseRequest?.requesterEmployee

  const initialData: WizardData = {
    description: purchaseOrder?.description || '',
    formattedPoNumber: purchaseOrder?.formattedPoNumber || '',
    errors: {
      taxId: undefined,
    },
    purchaseRequestCost: {
      cents: purchaseOrder?.purchaseRequest?.cost.cents ?? 0,
      currency: purchaseOrder?.purchaseRequest?.cost.currency ?? '',
    },
    legalEntityId: purchaseOrder?.legalEntity.id ?? undefined,
    url: purchaseOrder?.purchaseRequest?.url ?? undefined,
    companyId: company.id,
    purchaseOrderCost: {
      cents: purchaseOrder?.cost.cents ?? 0,
      currency: purchaseOrder?.cost.currency ?? '',
    },
    vendor: purchaseOrder?.vendor ?? undefined,
    additionalInformation: purchaseOrder?.purchaseRequest?.additionalInformation,
    preferredPaymentMethod: undefined,
    cardPaymentDetails: {
      cardholderData: {
        firstName: employee?.firstName,
        lastName: employee?.lastName,
        dateOfBirth: employee?.birthdayOn ? moment(employee.birthdayOn) : undefined,
        phoneCountryCode: employee?.phoneCode,
        phoneNumber: employee?.phoneNumber,
      },

      budgetFlexibility: 0,
      cardAlias: i18n.t(
        'procurement.purchase_orders.update_order_wizard.steps.payment_method.options.single_use_card.fields.card_alias.value',
        { vendor_name: purchaseOrder.vendor?.legalName }
      ),
      employeeId: employee?.id,
    },
    bankPaymentDetails: {
      bankCode: purchaseOrder.vendor?.bankCode,
      iban: purchaseOrder.vendor?.iban,
    },
  }

  const isVendorInfoCompleted = (data: WizardData): boolean => {
    return !!data.vendor
  }

  return (
    <Wizard.Modal<WizardData>
      initialData={initialData}
      locked={!!currentActivity}
      size="large"
      icon="document-edit"
      title={i18n.t('procurement.purchase_orders.update_order_wizard.title')}
      onClose={handleClose}
      onSubmit={handleSubmit}
      steps={compact([
        {
          title: i18n.t(
            'procurement.purchase_orders.update_order_wizard.steps.general_information.title'
          ),
          nextText: i18n.t(
            'procurement.purchase_orders.update_order_wizard.steps.general_information.next'
          ),
          render: (props) => <GeneralInformationStep {...props} />,
          isCompleted: isGeneralInfoCompleted,
        },
        {
          title: i18n.t('procurement.purchase_orders.update_order_wizard.steps.vendor.title'),
          nextText: i18n.t('procurement.purchase_orders.update_order_wizard.steps.vendor.next'),
          render: (props) => <VendorStep {...props} />,
          isCompleted: isVendorInfoCompleted,
        },
        {
          title: i18n.t('procurement.purchase_orders.update_order_wizard.steps.cost.title'),
          nextText: cardsEnabled
            ? i18n.t('procurement.purchase_orders.update_order_wizard.steps.cost.next')
            : i18n.t(
                'procurement.purchase_orders.update_order_wizard.steps.cost.create_purchase_order'
              ),
          render: (props) => (
            <CostStep
              legalEntities={purchaseOrderFormData?.apiCore.company?.legalEntities ?? []}
              currencies={
                purchaseOrderFormData?.apiCore.currenciesConnection.edges.map(
                  (edge) => edge.node
                ) ?? []
              }
              {...props}
            />
          ),
          isCompleted: isCostCompleted,
        },
        cardsEnabled
          ? {
              title: i18n.t(
                'procurement.purchase_orders.update_order_wizard.steps.payment_method.title'
              ),
              nextText: i18n.t(
                'procurement.purchase_orders.update_order_wizard.steps.payment_method.next'
              ),
              render: (props) => (
                <PaymentMethodStep
                  {...props}
                  vendor={purchaseOrder.vendor ?? undefined}
                  canCreateSingleUseCards={canCreateSingleUseCards ?? false}
                  canManageVendors={canManageContactsPaymentData ?? false}
                  hasCardsFeature={cardsEnabled}
                />
              ),
              isCompleted: isPaymentMethodCompleted,
            }
          : null,
      ])}
    />
  )
}
